import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import AdsterraNativeBanner from "../components/AdsterraNativeBanner";
import AdsterraBanner from "../components/AdsterraBanner";

import {
  Zap,
  Palette,
  MessageCircle,
  IndianRupee,
  Globe,
  ArrowRight,
  ChevronDown,
  Rocket,
  ShieldCheck,
  Smartphone,
  Server,
  MonitorSmartphone,
  Layers,
  Code2,
  PlayCircle,
  Linkedin,
  Crown,
  Users,
} from "lucide-react";

import AnimatedCard from '../components/AnimatedCard';
import SectionHeader from '../components/SectionHeader';
import Seo from '../seo/Seo';
import { HOME_SEO } from '../seo/pageSeo';

const WHY = [
  {
    icon: Zap,
    title: 'Fast Delivery',
    desc: 'Projects delivered with speed and precision.',
    color: '#00f5ff',
  },
  {
    icon: Palette,
    title: 'Premium Design',
    desc: 'Modern UI crafted for high conversions.',
    color: '#8b5cf6',
  },
  {
    icon: MessageCircle,
    title: 'Quick Support',
    desc: 'Fast communication and dedicated support.',
    color: '#f472b6',
  },
  {
    icon: IndianRupee,
    title: 'Affordable Pricing',
    desc: 'Premium quality at startup-friendly prices.',
    color: '#00f5ff',
  },
  {
    icon: Globe,
    title: 'Complete Solutions',
    desc: 'Everything from design to deployment.',
    color: '#8b5cf6',
  },
];

const SERVICES = [
  {
    icon: Globe,
    title: 'Website Development',
    color: '#00f5ff',
  },
  {
    icon: Smartphone,
    title: 'Mobile Apps',
    color: '#8b5cf6',
  },
  {
    icon: Server,
    title: 'Backend Systems',
    color: '#f472b6',
  },
  {
    icon: ShieldCheck,
    title: 'Secure APIs',
    color: '#00f5ff',
  },
  {
    icon: MonitorSmartphone,
    title: 'Responsive Design',
    color: '#8b5cf6',
  },
  {
    icon: Layers,
    title: 'Admin Dashboards',
    color: '#f472b6',
  },
];

const TECHS = [
  'React JS',
  'Next.js',
  'Node.js',
  'MongoDB',
  'Tailwind CSS',
  'React Native',
  'Firebase',
  'AWS',
  'Framer Motion',
  'SQL',
];

const PARTICLES = Array.from({ length: 25 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 5 + 2,
  color: ['#00f5ff', '#8b5cf6', '#f472b6'][i % 3],
  dur: (Math.random() * 3 + 2).toFixed(1),
  delay: (Math.random() * 4).toFixed(1),
}));

function InfiniteTechScroll() {

  const duplicated = [...TECHS, ...TECHS];

  return (
    <div className="relative overflow-hidden">

      <div className="absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-[#050810] to-transparent" />

      <div className="absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-[#050810] to-transparent" />

      <div
        className="flex gap-4 w-max"
        style={{
          animation: 'infiniteScroll 28s linear infinite'
        }}
      >

        {duplicated.map((tech, i) => (
          <div
            key={i}
            className="px-5 py-3 rounded-2xl border text-sm whitespace-nowrap"
            style={{
              border: '1px solid rgba(0,245,255,.15)',
              background: 'rgba(0,245,255,.05)',
              color: '#00f5ff'
            }}
          >
            {tech}
          </div>
        ))}

      </div>

    </div>
  );
}

export default function HomePage() {

  const randomWhy = useMemo(() => {
    return [...WHY].sort(() => Math.random() - 0.5);
  }, []);

  return (
    <>
      <Seo {...HOME_SEO} />
      {/* HERO */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-8 py-20"
      >

        {/* background */}
        <div className="absolute inset-0 overflow-hidden">

          <div className="absolute inset-0 bg-[#050810]" />

          <div
            className="absolute -left-60 top-1/3 w-[700px] h-[700px] rounded-full"
            style={{
              background: 'radial-gradient(circle,rgba(0,245,255,.13) 0%,transparent 70%)',
              filter: 'blur(55px)'
            }}
          />

          <div
            className="absolute -right-60 top-1/2 w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle,rgba(139,92,246,.15) 0%,transparent 70%)',
              filter: 'blur(55px)'
            }}
          />

          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle,rgba(244,114,182,.12) 0%,transparent 70%)',
              filter: 'blur(60px)'
            }}
          />

          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: 'linear-gradient(rgba(0,245,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,255,.03) 1px,transparent 1px)',
              backgroundSize: '50px 50px'
            }}
          />

          {PARTICLES.map(p => (
            <span
              key={p.id}
              className="absolute rounded-full"
              style={{
                left: `${p.left}%`,
                top: `${p.top}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                background: p.color,
                boxShadow: `0 0 ${p.size * 4}px ${p.color}`,
                animation: `dotPulse ${p.dur}s ease-in-out ${p.delay}s infinite alternate`,
                opacity: .6
              }}
            />
          ))}

        </div>


        {/* content */}
        <div className="relative z-10 max-w-6xl mx-auto text-center">


          <div
            className="inline-flex items-center gap-2 px-5 py-2 mb-8 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs tracking-widest"
          >
            <Rocket size={14} />
            MODERN DIGITAL SOLUTIONS
          </div>

          <h1
            className="text-white font-bold leading-tight mb-6"
            style={{
              fontSize: 'clamp(2.8rem,7vw,6rem)'
            }}
          >
            We Build <span className="grad-text">Modern</span> <br />
            Websites & Apps <br />
            For Your Business
          </h1>

          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed mb-10">
            VTCodex creates premium websites, applications and digital
            experiences designed to help businesses grow faster online.
          </p>

          <div className="flex justify-center gap-4 flex-wrap mb-16">

            <Link
              to="/projects"
              className="btn-primary no-underline"
            >
              View Projects
            </Link>

            <Link
              to="/contact"
              className="btn-secondary no-underline"
            >
              Contact Us
            </Link>

          </div>

          {/* stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">

            {[
              ['50+', 'Projects'],
              ['100+', 'Clients'],
              ['24/7', 'Support'],
              ['99%', 'Satisfaction'],
            ].map(([num, label], i) => (
              <div
                key={i}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm"
              >
                <h3 className="text-4xl font-bold grad-text mb-2">
                  {num}
                </h3>

                <p className="text-slate-400 text-sm">
                  {label}
                </p>
              </div>
            ))}

          </div>

          <div className="mt-16 flex justify-center">
            <ChevronDown
              size={22}
              color="#00f5ff"
              style={{
                animation: 'bounce 1.5s infinite'
              }}
            />
          </div>

        </div>

      </section>

      {/* SPECIALIZATION — the two things we actually build */}
      <section className="py-20 px-8 bg-[#0a0f1e] border-y border-white/5">

        <div className="max-w-6xl mx-auto">

          <SectionHeader
            label="WHAT WE SPECIALIZE IN"
            title="Websites & Apps. That's It."
            sub="We don't spread thin across everything — we go deep on these two."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">

            <AnimatedCard
              className="group relative overflow-hidden rounded-3xl border p-8 sm:p-10 transition-all duration-500 hover:-translate-y-2"
              style={{ borderColor: 'rgba(0,245,255,.18)', background: 'rgba(0,245,255,.04)' }}
            >
              <div
                className="absolute top-0 right-0 w-52 h-52 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"
                style={{ background: 'radial-gradient(circle,rgba(0,245,255,.18),transparent 70%)', transform: 'translate(30%,-30%)' }}
              />

              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                style={{ background: 'rgba(0,245,255,.12)', border: '1px solid rgba(0,245,255,.3)' }}>
                <Code2 size={30} color="#00f5ff" />
              </div>

              <h3 className="text-white text-2xl sm:text-3xl font-bold mb-3">Website Development</h3>
              <p className="text-slate-400 leading-relaxed mb-8">
                Fast, modern, conversion-focused websites for businesses,
                schools, colleges and startups — built on React, deployed and
                SEO-ready from day one.
              </p>

              <Link to="/projects" className="inline-flex items-center gap-2 font-bold no-underline" style={{ color: '#00f5ff' }}>
                See Live Websites <ArrowRight size={16} />
              </Link>
            </AnimatedCard>

            <AnimatedCard
              delay={0.1}
              className="group relative overflow-hidden rounded-3xl border p-8 sm:p-10 transition-all duration-500 hover:-translate-y-2"
              style={{ borderColor: 'rgba(139,92,246,.18)', background: 'rgba(139,92,246,.04)' }}
            >
              <div
                className="absolute top-0 right-0 w-52 h-52 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"
                style={{ background: 'radial-gradient(circle,rgba(139,92,246,.18),transparent 70%)', transform: 'translate(30%,-30%)' }}
              />

              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                style={{ background: 'rgba(139,92,246,.12)', border: '1px solid rgba(139,92,246,.3)' }}>
                <Smartphone size={30} color="#8b5cf6" />
              </div>

              <h3 className="text-white text-2xl sm:text-3xl font-bold mb-3">Mobile App Development</h3>
              <p className="text-slate-400 leading-relaxed mb-8">
                End-to-end React Native apps with real backends and push
                notifications — designed, built and published, live on the
                Google Play Store.
              </p>

              <Link to="/apps" className="inline-flex items-center gap-2 font-bold no-underline" style={{ color: '#8b5cf6' }}>
                <PlayCircle size={16} /> See Live Apps <ArrowRight size={16} />
              </Link>
            </AnimatedCard>

          </div>

        </div>

      </section>

      {/* TECH SCROLL */}
      <section className="py-10 bg-[#050810] overflow-hidden border-y border-white/5">

        <InfiniteTechScroll />

      </section>

      {/* ABOUT */}
      <section className="py-24 px-8 bg-[#0a0f1e]">

        <div className="max-w-7xl mx-auto">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="section-label">
                ABOUT VTCODEX
              </span>

              <h2 className="text-white font-bold leading-tight mt-4 mb-6 text-5xl">
                Your Complete <br />
                <span className="grad-text">
                  Digital Partner
                </span>
              </h2>

              <p className="text-slate-400 leading-relaxed mb-8">
                We help businesses, schools, startups and brands
                establish a premium online presence with modern
                technologies and high-performance systems.
              </p>

              <div className="flex flex-wrap gap-3">

                {TECHS.map((tech, i) => (
                  <span
                    key={i}
                    className="px-4 py-2 rounded-full text-sm"
                    style={{
                      background: 'rgba(0,245,255,.06)',
                      border: '1px solid rgba(0,245,255,.15)',
                      color: '#00f5ff'
                    }}
                  >
                    {tech}
                  </span>
                ))}

              </div>

            </div>

            {/* right cards */}
            <div className="grid grid-cols-2 gap-5">

              {SERVICES.map((s, i) => (
                <AnimatedCard
                  key={i}
                  delay={i * 0.08}
                  className="group rounded-3xl border border-white/10 bg-[#0d1428] p-8 transition-all duration-500 hover:-translate-y-3"
                >

                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                    style={{
                      background: `${s.color}15`,
                      border: `1px solid ${s.color}30`
                    }}
                  >
                    <s.icon size={30} color={s.color} />
                  </div>

                  <h3 className="text-white text-xl font-bold mb-3">
                    {s.title}
                  </h3>

                  <button
                    className="flex items-center gap-2 mt-5"
                    style={{
                      color: s.color
                    }}
                  >
                    Explore
                    <ArrowRight size={16} />
                  </button>

                </AnimatedCard>
              ))}

            </div>

          </div>

        </div>

      </section>

      {/* WHY */}
      <section className="py-24 px-8 bg-[#050810]">

        <div className="max-w-7xl mx-auto">

          <SectionHeader
            label="WHY CHOOSE US"
            title="Built For Modern Brands"
            sub="Premium solutions with modern technologies and futuristic UI."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">

            {randomWhy.map((w, i) => (
              <AnimatedCard
                key={i}
                delay={i * 0.08}
                className="group relative rounded-3xl overflow-hidden border border-white/10 bg-[#0d1428] p-8 transition-all duration-500 hover:-translate-y-3"
              >

                <div
                  className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"
                  style={{
                    background: `radial-gradient(circle,${w.color}20,transparent 70%)`,
                    transform: 'translate(30%,-30%)'
                  }}
                />

                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background: `${w.color}15`,
                    border: `1px solid ${w.color}30`
                  }}
                >
                  <w.icon size={30} color={w.color} />
                </div>

                <h3 className="text-white text-2xl font-bold mb-4">
                  {w.title}
                </h3>

                <p className="text-slate-400 leading-relaxed">
                  {w.desc}
                </p>

              </AnimatedCard>
            ))}

          </div>

        </div>

      </section>

      {/* FOUNDERS + LIVE APPS (SEO: founder / co-founder entity) */}
      <section className="py-24 px-8 bg-[#0a0f1e]" aria-labelledby="founders-heading">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            label="WHO IS BEHIND VTCODEX"
            title="Founders of VTCodex"
            sub="Vishal Rana (Founder) and Tanisha Garg (Co-Founder)"
          />

          <p className="text-slate-400 text-center max-w-3xl mx-auto leading-relaxed mb-12">
            <strong className="text-white">VTCodex</strong> was founded by{' '}
            <strong className="text-white">Vishal Rana</strong> and{' '}
            <strong className="text-white">Tanisha Garg</strong>. Together they lead a team that builds
            websites and Android apps for schools, colleges, institutes and businesses across India –
            including live apps on Google Play.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: 'Vishal Rana', role: 'Founder of VTCodex', Icon: Crown, color: '#00f5ff', url: 'https://www.linkedin.com/in/vishalrana227' },
              { name: 'Tanisha Garg', role: 'Co-Founder of VTCodex', Icon: Users, color: '#8b5cf6', url: 'https://www.linkedin.com/in/tanisha-garg-70116b313' },
            ].map((f, i) => (
              <AnimatedCard
                key={f.name}
                delay={i * 0.1}
                className="rounded-[2rem] border border-white/10 bg-[#0d1428] p-8 text-center transition-all duration-500 hover:-translate-y-2"
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: `${f.color}15`, border: `1px solid ${f.color}35` }}
                >
                  <f.Icon size={28} color={f.color} />
                </div>
                <h3 className="text-white text-2xl font-bold mb-1">{f.name}</h3>
                <p className="text-sm tracking-widest uppercase mb-6" style={{ color: f.color }}>{f.role}</p>
                <div className="flex justify-center gap-3 flex-wrap">
                  <Link to="/team" className="btn-secondary no-underline">View Profile</Link>
                  <a
                    href={f.url}
                    target="#"
                    rel="me noopener noreferrer"
                    aria-label={`${f.name} on LinkedIn`}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold no-underline"
                    style={{ background: 'rgba(10,102,194,.12)', border: '1px solid rgba(10,102,194,.45)', color: '#4aa3ff' }}
                  >
                    <Linkedin size={16} /> LinkedIn
                  </a>
                </div>
              </AnimatedCard>
            ))}
          </div>

          <div className="mt-14 text-center">
            <h3 className="text-white text-2xl font-bold mb-3">Apps by VTCodex on Google Play</h3>
            <p className="text-slate-400 max-w-2xl mx-auto mb-6">
              VTCodeX Restro (restaurant billing) and Rasdhara (cattle feed ordering) are live on the Play Store –
              built end-to-end by VTCodex.
            </p>
            <Link to="/apps" className="btn-primary no-underline">
              <PlayCircle size={18} /> View Play Store Apps
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-8 pb-24 bg-[#050810]">

        <div className="max-w-5xl mx-auto">

          <AnimatedCard
            className="rounded-[2rem] border border-cyan-400/10 bg-cyan-400/[0.04] p-12 text-center"
          >

            <h2 className="text-white text-5xl font-bold mb-5">
              Let’s Build Something Amazing
            </h2>

            <p className="text-slate-400 max-w-2xl mx-auto mb-10">
              Ready to transform your business with premium digital solutions?
            </p>

            <div className="flex justify-center gap-4 flex-wrap">

              <Link
                to="/contact"
                className="btn-primary no-underline"
              >
                Start Your Project
              </Link>

              <Link
                to="/projects"
                className="btn-secondary no-underline"
              >
                Explore Work
              </Link>

            </div>

          </AnimatedCard>

        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
          <AdsterraBanner />
          <AdsterraBanner />
          <AdsterraBanner />
          <AdsterraBanner />
        </div>

        <AdsterraNativeBanner />

      </section>

      <style>{`

        @keyframes dotPulse {
          from {
            opacity:.25;
            transform:scale(1);
          }
          to {
            opacity:.8;
            transform:scale(1.8);
          }
        }

        @keyframes infiniteScroll {
          from {
            transform:translateX(0);
          }
          to {
            transform:translateX(calc(-50% - 8px));
          }
        }

        @keyframes bounce {
          0%,100% {
            transform:translateY(0);
          }
          50% {
            transform:translateY(10px);
          }
        }

      `}</style>
    </>
  );
}